import { useLazyQuery, type ApolloError } from '@apollo/client'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  GET_CATALOG,
  toReorderLines,
  type ICatalog,
  type IProductsPricedPerCollectionVariables,
  type ReorderLine,
} from '@commerce/core'

export interface UseReorderAvailability {
  /**
   * Resolves every sku against the catalog — or `null` when the catalog could
   * not be read, which is "unknown", not "unavailable".
   */
  check: (quantities: Readonly<Record<string, number>>) => Promise<ReorderLine[] | null>
  loading: boolean
  error: ApolloError | undefined
}

/**
 * What of an old order the storefront still sells.
 *
 * An order records what *was* bought, so time is the whole problem: a sku can
 * have been delisted or gone out of stock since it was placed. Writing those
 * lines straight back into a cart produces a cart short a couple of items with
 * nothing on screen saying why, so the catalog is consulted first and every
 * line resolved against it.
 *
 * **The whole catalog, deliberately.** `GET_CATALOG` is not cheap and the home
 * screen already holds page one of it — but page one is not enough to answer
 * this question. A sku sitting on page two is *absent* from page one, and
 * reading that absence as `notInCatalog` invents a claim about a product that
 * is still perfectly sellable. Paying for the full read on an explicit tap is
 * the cost of not lying.
 *
 * Lazy for the same reason: it is the tap that pays, not the visit. A shopper
 * who never asks to repeat anything never fetches a catalog to find out.
 *
 * A failed read returns `null` rather than a list of unavailable lines, so a
 * caller cannot mistake a network error for an answer about stock.
 */
export const useReorderAvailability = (): UseReorderAvailability => {
  const { slug = '', sessionId = '' } = useParams()

  const [fetchCatalog, { loading, error }] = useLazyQuery<
    ICatalog,
    IProductsPricedPerCollectionVariables
  >(GET_CATALOG, {
    variables: {
      catalogStorefrontName: slug,
      catalogSessionId: sessionId,
      totalPromotionsSessionId: sessionId,
    },
  })

  const check = useCallback(
    async (quantities: Readonly<Record<string, number>>) => {
      const result = await fetchCatalog()
      // `useLazyQuery` resolves with no data on failure rather than throwing.
      // Returning `null` keeps that distinguishable from an empty catalog,
      // which is a real answer and would mark every line delisted.
      if (!result.data) return null
      return toReorderLines(quantities, result.data.catalog)
    },
    [fetchCatalog],
  )

  return { check, loading, error }
}
